import { FilterQuery } from '@/network/interfaces/FilterQuery'
import { KeyFilter } from '@/network/interfaces/KeyFilter'
import { SortQuery } from '@/network/interfaces/SortQuery'
import { modifyCollection, modifiyDocumentById, modifyDocuments, queryAll, queryDocuments, uploadDocuments, deleteDocument, deleteDocuments, queryDocumentById, postFile, getFile, deleteDocumentFile, aggregateQuery, putObject, querySize, getFileBinaryContent } from '@/network/util/defectDBUtils'
import { PageQuery } from '@/network/interfaces/PageQuery'
import { Timestamp } from '@/interfaces/Timestamp'
import { $in, like } from '@/network/util/QueryBuilder'
import { aggregate } from '../restHeart/aggregate'
import { sliceArray } from '@/util/sliceArray'
import { AxiosRequestConfig } from 'axios'
/**
 * 从Base中选取类型为Condition的属性，组成新的类型
 */
type SubType<Base, Condition> = Pick<
	Base,
	{
		[Key in keyof Base]: Base[Key] extends Condition ? Key : never
	}[keyof Base]
>

/**
 * 从Base中排除类型为Condition的属性，组成新的类型
 */
type ExcludeSubType<Base, Condition> = Pick<
	Base,
	{
		[Key in keyof Base]: Base[Key] extends Condition ? never : Key
	}[keyof Base]
>

type SortKeys<T> = { [K in keyof T]: 1 | -1 }
type KeysFilter<T> = { [K in keyof T]: 1 | 0 }

export class BaseRequest<T> {
	private readonly collection: string

	/**
	 * 构造文档请求对象。
	 * @param collection
	 */
	public static build = <T>(collection: string) => {
		return new BaseRequest<T>(collection)
	}

	/**
	 * 构造函数。
	 * @param collection
	 */
	constructor(collection: string) {
		this.collection = collection
	}

	/**
	 * 某些对象上只有关联 id，没有详细数据时，可以将 id 收集起来，通过本方法查询它们的详细数据。
	 * @returns 返回id映射到数据的 Map ， key 为 id , value为数据对象
	 */
	async queryIdMapByIds(ids: string[], keys?: KeysFilter<T>) {
		const objectList = await this.queryByIds(ids, keys)
		return objectList.reduce((acc, curr: any) => {
			acc.set((curr._id && curr._id.$oid) || '_', curr)
			return acc
		}, new Map<string, T>())
	}

	async queryAllToMap(filter?: FilterQuery, keys?: KeyFilter, sort?: SortQuery, sizePerRequest = 100) {
		const all = await this.queryAll(filter, keys, sort, sizePerRequest)
		return all.reduce((acc, curr) => {
			acc[(curr as any)._id.$oid] = curr
			return acc
		}, {} as { [props: string]: T })
	}

	async queryAllAndGroup(groupKey: string, filter?: FilterQuery, keys?: KeyFilter, sizePerRequest = 1000) {
		return this.queryAll(filter, keys, undefined, sizePerRequest).then(all =>
			all.reduce((acc, curr) => {
				if (acc[curr[groupKey]] === undefined) {
					acc[curr[groupKey]] = []
				}
				acc[curr[groupKey]].push(curr)
				return acc
			}, {} as any)
		)
	}
	/**
	 * 筛选 `filterKey` in inIds中的记录，inIds 较大时会抛出413错误，此时可以使用本方法。
	 */
	async sliceQueryAll(filterKey: string, inIds: string[], filter: any, keys: any) {
		const allSlice = sliceArray(inIds, 200)
		return (
			await Promise.all(
				allSlice.map(idListSlice => {
					return this.queryAll(
						{
							[filterKey]: { $in: idListSlice },
							...filter,
						},
						keys
					)
				})
			)
		).flat()
	}

	/**
	 * 分页搜索查询
	 * @param pagesize 每页大小
	 * @param page 页码
	 * @param textSearch 字符模糊搜索， 如 {name: '王', state: '吃饭'}, 表示搜索 name 中包含 '王' 且 state 中包含 '吃饭' 的对象
	 * @param inValuesSearch 值搜索，如 { name: ['王大锤', '李小刀'], state: ['我在吃饭', '去哪儿吃饭'] }，表示搜索 name 为 王大锤 或 李小刀， 且 state 为 我在吃饭 或 去哪儿吃饭 的对象
	 * @param sortByKeys 按照指定属性进行排序，-1 倒序，1 正序
	 * @param keys 指定返回数据中包含或排除的属性，0 排除，1 包含
	 */
	searchPage(pagesize: number, page: number, textSearch?: SubType<T, string>, inValuesSearch?: { [K in keyof ExcludeSubType<T, Timestamp>]: T[K][] }, sortByKeys?: SortKeys<T>, keys?: KeysFilter<T>) {
		const likeSearch =
			textSearch &&
			Object.keys(textSearch).reduce((acc: any, curr) => {
				const searchString = textSearch[curr]
				if (searchString) {
					const likeString = like(searchString)
					if (acc === null) {
						return { [curr]: likeString }
					}
					acc[curr] = likeString
				}
				return acc
			}, null)
		let filter
		if (likeSearch) {
			filter = likeSearch
		}
		const inSearch =
			inValuesSearch &&
			Object.keys(inValuesSearch).reduce((acc: any, curr) => {
				const values = inValuesSearch[curr]
				if (values.length > 0) {
					const inValues = $in(values)
					if (acc === null) {
						return { [curr]: inValues }
					}
					acc[curr] = inValues
				}
				return acc
			}, null)
		if (filter) {
			filter = { ...filter, ...inSearch }
		} else {
			filter = inSearch
		}
		return this.queryPage({
			page,
			pagesize,
			filter,
			sort: sortByKeys,
			keys,
		})
	}

	/**
	 * 分页查询。
	 * @param query
	 */
	queryPage(query: PageQuery) {
		return queryDocuments<T>(this.collection, query)
	}

	/**
	 * 查询集合符合条件的全部数据，此方法仅适合用于查询总量较小的数据
	 * @param filter
	 * @param keys
	 * @param sort
	 * @param sizePerRequest
	 */
	queryAll(filter?: FilterQuery, keys?: KeyFilter, sort?: SortQuery, sizePerRequest = 100) {
		return queryAll<T>(this.collection, sizePerRequest, filter, keys, sort)
	}

	/**
	 * 根据id列表查询
	 * @param ids
	 * @param keys
	 * @param sort
	 * @param sizePerRequest
	 */
	queryByIds(ids: string[], keys?: KeyFilter, sort?: SortQuery, sizePerRequest = 100) {
		return this.queryAll({ _id: { $in: ids.map(id => ({ $oid: id })) } }, keys, sort, sizePerRequest)
	}

	/**
	 * 根据Id查询文档。
	 * @param id
	 */
	getDocumentById(id: string) {
		return queryDocumentById(this.collection, id)
	}

	/**
	 * 批量上传数据
	 * @param dataList 对象列表
	 */
	uploadBatch(dataList: T[]) {
		return uploadDocuments(this.collection, dataList)
	}

	/**
	 * 上传数据，返回对象id
	 * @param data 数据对象
	 */
	async upload(data: T): Promise<string> {
		const {
			headers: { location },
		} = await uploadDocuments(this.collection, data)

		if (location) {
			const list = location.split('/')
			return list[list.length - 1]
		}

		return ''
	}

	/**
	 * 根据id进行修改
	 * @param id 要修改的对象id
	 * @param data 数据对象
	 */
	modifyById(id: string, data: any) {
		return modifiyDocumentById(this.collection, id, data)
	}

	/**
	 * 根据条件批量修改
	 * @param filter 修改条件
	 * @param data 数据对象
	 */
	modifyBatch(filter: FilterQuery, data: any) {
		return modifyDocuments(this.collection, filter, data)
	}

	/**
	 * 根据文档Id删除。
	 * @param id 文档Id。
	 */
	deleteById(id: string) {
		return deleteDocument(this.collection, id)
	}

	/**
	 * 按条件进行批量删除。
	 * @param filter
	 */
	deleteBatch(filter: FilterQuery) {
		return deleteDocuments(this.collection, filter)
	}

	/**
	 * 上传文件到 collection.files 中，返回文件 id。
	 * @param file
	 * @param contentType
	 * @param fileId
	 * @param onProgress
	 */
	async uploadFile(file: Blob, contentType?: string, fileId?: string, onProgress = () => {}) {
		const {
			headers: { location },
		} = await postFile(this.collection, file, fileId, contentType, onProgress)

		if (location) {
			const list = location.split('/')
			return list[list.length - 1]
		}

		return fileId
	}

	/**
	 * 根据文件Id获取文件。
	 * @param fileId
	 * @param requestConfig
	 */
	getFileContentById(fileId: string, requestConfig?: AxiosRequestConfig | undefined) {
		return getFile(this.collection, fileId, requestConfig)
	}

	/**
	 * 根据文件Id删除文件 。
	 * @param fileId 文件Id。
	 */
	deleteFileById(fileId: string) {
		return deleteDocumentFile(this.collection, fileId)
	}

	/**
	 *
	 * @param aggregateName
	 * @param data
	 */
	aggregateQuery(aggregateName: string, data?: any) {
		return aggregateQuery(this.collection, aggregateName, data)
	}

	/**
	 * 向指定集合中添加一个聚合管道
	 * @param aggregateSetting :
        {
            "stages" : [
            ],
            "type" : "pipeline",
            "uri" : "...",
            "allowDiskUse": true/false
        }
	 */
	async addPipeline(stages: any[], uri: string, allowDiskUse = true) {
		let {
			data: { aggrs },
		} = await this.queryPage({
			pagesize: 0,
		})
		const pipeline = {
			stages,
			uri,
			type: 'pipeline',
			allowDiskUse,
		}
		if (aggrs === undefined || aggrs === null) {
			aggrs = []
		}
		// 检查是否已经有相同 uri
		console.log('aggrs:', aggrs)
		const aggr = aggrs.find(aggr => aggr.uri === uri)
		if (aggr !== undefined) {
			console.warn(`${this.collection} 集合中，已经存在 uri 为 ${uri} 的聚合查询，放弃创建，复用已存在的聚合查询。`)
			return Promise.resolve()
		}
		aggrs.push(pipeline)
		return modifyCollection(this.collection, { aggrs })
	}

	/**
	 *
	 * @param data
	 */
	putObject(data: any) {
		return putObject(this.collection, data)
	}

	/**
	 *
	 * @param filter
	 */
	getSize(filter: FilterQuery) {
		return querySize(this.collection, filter)
	}

	/**
	 *
	 * @param fileId
	 */
	getFileBinary(fileId: string) {
		return getFileBinaryContent(this.collection, fileId)
	}
}
